import { profileClient } from '@/lib/api/profileClient'
import {
    MIN_SHOWN,
    PERIODS,
    widen,
    windowDays,
    type Period
} from '@/lib/analytics/periods'
import {
    requestsReceived,
    totalsBy,
    visitors,
    workoutsDone
} from '@/lib/analytics/queryItems'
import { DAY_MS, HOUR_MS, getNowUTC } from '@/lib/date'

import type { Author } from '@/domain'

/**
 * The leaderboard is the app's period stats, ranked: athletes by workouts
 * done, coaches by plan requests received (the coaches "trending" stat),
 * each among their own kind (`profileClient.listCoachIds`), over today /
 * this week / this month. Next to the athletes, who joined in the period.
 * Totals come from /sm (opt-outs are left out there), profiles from the
 * PocketBase proxy.
 *
 * Being active (opening the app, a visit, or having a total) and being
 * ranked are separate: people active in the window with nothing to rank
 * follow the ranked rows, unranked. A window with fewer than MIN_SHOWN
 * people widens to the next one, and says so.
 */

export const BOARDS = ['athletes', 'coaches'] as const
export type Board = (typeof BOARDS)[number]

/** Rows a board lists; the caller's own place is known past it. */
export const TOP = 10

// Profiles read past the top, to fill it when some are gone.
const MISSING_SLACK = 5

/** Faces the newcomers grid shows (home shows the first few); the count
 * covers everyone who joined. */
export const NEWCOMER_FACES = 20

// What each board counts, over a window.
const TOTALS: Record<
    Board,
    (window: string) => ReturnType<typeof workoutsDone>
> = {
    athletes: (window) => workoutsDone(window, 'userId'),
    coaches: (window) => requestsReceived(window)
}

/** Someone on a board. */
export type Person = {
    id: string
    login: string
    photo?: string
    level?: string
}

/** Someone ranked: their total and competition rank (equal totals share). */
export type Entry = Person & { rank: number; value: number }

export type BoardData = {
    entries: Entry[]
    /** Active in the window with no total: listed after the ranked, up to
     * `top` rows in all. */
    active: Person[]
    /** People with a total in the window. */
    total: number
    /** The caller's rank among them; undefined when they have none. */
    myRank?: number
    /** The window shown: the period's, or a wider one when it was quiet. */
    window: string
    widened: boolean
}

export function isBoard(value: string | null): value is Board {
    return !!value && (BOARDS as readonly string[]).includes(value)
}

/** The leaderboard page, optionally on a board and period. */
export function leaderboardPath(board?: Board, period?: Period): string {
    const params = new URLSearchParams()
    if (board) params.set('tab', board)
    if (period) params.set('period', period)
    const query = params.toString()
    return `/coaches/leaderboard${query ? `?${query}` : ''}`
}

/** Competition ranks over totals sorted highest first: 1, 1, 3. */
export function competitionRanks(totals: number[]): number[] {
    const out: number[] = []
    totals.forEach((total, i) =>
        out.push(i > 0 && totals[i - 1] === total ? out[i - 1] : i + 1)
    )
    return out
}

// Who is a coach changes rarely: read once a minute, shared by every board
// and the home teaser.
const COACHES_TTL = 60_000
let coaches: { at: number; ids: Promise<Set<string>> } | undefined

function coachIds(): Promise<Set<string>> {
    const now = getNowUTC()
    if (!coaches || now - coaches.at >= COACHES_TTL) {
        const ids = profileClient.listCoachIds()
        coaches = { at: now, ids }
        ids.catch(() => (coaches = undefined))
    }
    return coaches.ids
}

export async function loadBoard(
    board: Board,
    period: Period,
    myId: string,
    top = TOP
): Promise<BoardData> {
    const coaches = coachIds()
    // The board over one window, profiles resolved: a window is judged by
    // the people it can show.
    const inWindow = async (window: string) => {
        const [items, visits, coachSet] = await Promise.all([
            TOTALS[board](window),
            // Visits only add rows; a failed read leaves the ranking.
            visitors(window).catch(() => []),
            coaches
        ])
        // Two boards, two kinds of people: coaches rank only among coaches,
        // even when they train.
        const ofKind = (id: string) =>
            coachSet.has(id) === (board === 'coaches')
        const ranked = totalsBy(items, 'userId').filter((e) => ofKind(e.id))
        const rankedIds = new Set(ranked.map((e) => e.id))
        // Most days active first.
        const active = totalsBy(visits, 'userId')
            .map((e) => e.id)
            .filter((id) => ofKind(id) && !rankedIds.has(id))

        // Profiles for the top and a few more: one the proxy does not return
        // (banned, deleted) leaves the board, so ranks and totals have no
        // gaps. The active fill what the ranked leave.
        const candidates = ranked.slice(0, top + MISSING_SLACK)
        const activeCandidates = active.slice(
            0,
            Math.max(0, top + MISSING_SLACK - candidates.length)
        )
        const authors = new Map(
            (
                await profileClient.fetchAuthors([
                    ...candidates.map((e) => e.id),
                    ...activeCandidates
                ])
            ).map((a) => [a.id, a])
        )
        const person = (id: string): Person[] => {
            const author = authors.get(id)
            return author
                ? [
                      {
                          id,
                          login: author.login || '',
                          photo: author.photo,
                          level: author.level
                      }
                  ]
                : []
        }
        const totals = ranked.filter(
            (e, i) => i >= candidates.length || authors.has(e.id)
        )
        const ranks = competitionRanks(totals.map((e) => e.total))
        const me = totals.findIndex((e) => e.id === myId)
        const entries = totals.slice(0, top).flatMap((e, i) =>
            person(e.id).map((p) => ({
                ...p,
                rank: ranks[i],
                value: e.total
            }))
        )
        return {
            entries,
            active: activeCandidates
                .flatMap(person)
                .slice(0, Math.max(0, top - entries.length)),
            total: totals.length,
            myRank: me < 0 ? undefined : ranks[me]
        }
    }
    const { window, widened, value } = await widen(
        PERIODS[period],
        inWindow,
        (b) => b.entries.length + b.active.length >= Math.min(MIN_SHOWN, top)
    )
    return { ...value, window, widened }
}

export type Newcomers = {
    people: Author[]
    total: number
    /** The window they joined in; undefined: the newest ever, any time. */
    window?: string
    /** Not the period's window: a wider one, or any time. */
    widened: boolean
}

/**
 * Who joined in the period (the same rolling window as the boards), newest
 * first, and how many in all. Fewer than MIN_SHOWN widens the window, and
 * past the widest it is simply the newest members. Cutoffs are rounded down
 * to the hour and the page size is fixed, so home and the board share one
 * proxied query (and its cache entry) per window for an hour.
 */
export async function loadNewcomers(period: Period): Promise<Newcomers> {
    const hour = Math.floor(getNowUTC() / HOUR_MS) * HOUR_MS
    const since = (window: string) => hour - windowDays(window) * DAY_MS
    const enough = (n: { total: number }) => n.total >= MIN_SHOWN
    const { window, value, widened } = await widen(
        PERIODS[period],
        (w) => profileClient.listNewcomers(since(w), NEWCOMER_FACES),
        enough
    )
    if (enough(value)) return { ...value, window, widened }
    const newest = await profileClient.listNewcomers(0, NEWCOMER_FACES)
    return { ...newest, widened: true }
}

/** The caller on home: hidden, or their own board (their place on it). */
export type TeaserMe =
    | { hidden: true }
    | { hidden: false; board: Board; own: BoardData }

export type TeaserData = {
    athletes: BoardData
    newcomers: Newcomers
    me: TeaserMe
}

/**
 * Home: the week's top three athletes, who joined this week, and the
 * caller's place this week: on the athletes board, or on the coaches one
 * for a coach (never ranked as an athlete), or that they opted out.
 */
export async function loadTeaser(myId: string): Promise<TeaserData> {
    const [athletes, newcomers, coachBoard, hidden] = await Promise.all([
        loadBoard('athletes', 'week', myId, 3),
        loadNewcomers('week'),
        // A coach's place is the one their board shows: same window, same
        // rank.
        coachIds().then((ids) =>
            ids.has(myId) ? loadBoard('coaches', 'week', myId) : undefined
        ),
        profileClient
            .fetchAuthor(myId)
            .then((me) => !!me?.leaderboardHidden)
            .catch(() => false)
    ])
    const me: TeaserMe = hidden
        ? { hidden: true }
        : coachBoard
          ? { hidden: false, board: 'coaches', own: coachBoard }
          : { hidden: false, board: 'athletes', own: athletes }
    return { athletes, newcomers, me }
}
